import React from "react";
import { motion, AnimatePresence } from "framer-motion";

/* PixelBurst — sprinkles tiny coloured squares from an origin point outward.
 * Two modes:
 *  - Decorative (default): renders inside a positioned ancestor, anchors to a
 *    corner. Used on the Hero on initial load.
 *  - Burst-on-trigger: pass `trigger` (any changing value) to fire a fresh
 *    burst from the centre of the anchor whenever it changes. Used by the
 *    "added to cart" pop and the success page celebration.
 *
 *  This module is pure CSS + framer-motion; no external libs.
 */

export const BURST_COLORS = [
  "var(--st-mustard)",
  "var(--st-maroon)",
  "var(--st-gold)",
  "var(--st-orange)",
  "var(--st-cream)",
  "var(--st-fg)",
];

const seededRand = (seed) => {
  const x = Math.sin(seed * 9999.13) * 43758.5453;
  return x - Math.floor(x);
};

/* Build a list of pixel particles given the visual config. */
const buildParticles = ({
  count,
  spread,
  origin,
  baseDelay,
  size: [minSize, maxSize],
  colors,
  seedOffset = 0,
}) => {
  const list = [];
  for (let i = 0; i < count; i++) {
    const s = i + 1 + seedOffset;
    const angle = seededRand(s) * Math.PI * 2;
    const distance = spread[0] + seededRand(s + 33) * (spread[1] - spread[0]);
    const horiz = origin.includes("right") ? -1 : origin.includes("left") ? 1 : 1;
    const vert = origin.includes("bottom") ? -1 : 1;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const dx = origin === "center" ? cos * distance : cos * distance * horiz;
    const dy =
      origin === "center"
        ? sin * distance
        : Math.abs(sin) * distance * vert + seededRand(s + 41) * 40 * vert;
    const size = minSize + Math.floor(seededRand(s + 7) * (maxSize - minSize));
    list.push({
      dx,
      dy,
      size,
      color: colors[Math.floor(seededRand(s + 11) * colors.length)],
      delay: baseDelay + seededRand(s + 5) * 0.5,
      duration: 0.9 + seededRand(s + 13) * 0.8,
    });
  }
  return list;
};

const cornerClass = (origin) => {
  switch (origin) {
    case "top-left":
      return "top-0 left-0";
    case "top-right":
      return "top-0 right-0";
    case "bottom-left":
      return "bottom-0 left-0";
    case "bottom-right":
      return "bottom-0 right-0";
    case "center":
      return "top-1/2 left-1/2";
    default:
      return "top-0 right-0";
  }
};

/* Decorative burst — renders once on mount. */
export const PixelBurst = ({
  origin = "top-right",
  count = 28,
  spread = [160, 520],
  size = [8, 26],
  baseDelay = 0.4,
  colors = BURST_COLORS,
  zIndex = 25,
}) => {
  const pixels = React.useMemo(
    () => buildParticles({ count, spread, origin, baseDelay, size, colors }),
    [count, spread, origin, baseDelay, size, colors]
  );

  return (
    <div
      aria-hidden
      className={`absolute ${cornerClass(origin)} pointer-events-none`}
      style={{ width: 1, height: 1, zIndex }}
    >
      {pixels.map((p, i) => (
        <motion.span
          key={i}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0, rotate: 0 }}
          animate={{
            x: p.dx,
            y: p.dy,
            opacity: [0, 1, 1, 0],
            scale: [0, 1.2, 1, 0.4],
            rotate: 90,
          }}
          transition={{
            delay: p.delay,
            duration: p.duration,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.25, 0.75, 1],
          }}
          className="block absolute"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            top: 0,
            left: 0,
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}
    </div>
  );
};

/* Triggered burst — re-fires every time `trigger` changes. Always emits from
 * the centre of its anchor. Good for "added to cart" feedback. */
export const PixelPop = ({
  trigger,
  count = 14,
  spread = [40, 120],
  size = [5, 12],
  colors = BURST_COLORS,
  zIndex = 30,
}) => {
  // Generate a brand-new key whenever trigger changes so AnimatePresence
  // mounts a fresh particle set (the previous one fades out cleanly).
  const key = String(trigger ?? "init");

  return (
    <div
      aria-hidden
      className="absolute top-1/2 left-1/2 pointer-events-none"
      style={{ width: 1, height: 1, zIndex }}
    >
      <AnimatePresence>
        {trigger != null && (
          <PixelBurstInstance
            key={key}
            count={count}
            spread={spread}
            size={size}
            colors={colors}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const PixelBurstInstance = ({ count, spread, size, colors }) => {
  // unique seed per mount so successive bursts feel different
  const seedOffset = React.useMemo(() => Math.floor(Math.random() * 9999), []);
  const pixels = React.useMemo(
    () =>
      buildParticles({
        count,
        spread,
        origin: "center",
        baseDelay: 0,
        size,
        colors,
        seedOffset,
      }),
    [count, spread, size, colors, seedOffset]
  );

  return (
    <>
      {pixels.map((p, i) => (
        <motion.span
          key={i}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
          animate={{
            x: p.dx,
            y: p.dy,
            opacity: [0, 1, 0],
            scale: [0, 1.3, 0.3],
            rotate: 180,
          }}
          exit={{ opacity: 0 }}
          transition={{
            delay: p.delay,
            duration: 0.9,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.3, 1],
          }}
          className="block absolute"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            top: 0,
            left: 0,
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}
    </>
  );
};
